import { BASE_CONFIG } from '@iheartjane/reefer';
import type {
  BaseTypographyVariant,
  BoldTypographyVariant,
  TypographyVariant,
  TypographyVariantComponentMap,
  TypographyVariantConfig,
} from '@iheartjane/reefer';

import { ObjectKeys } from '@jane/shared/util';

/**
 * At mobile breakpoints, which apply to iPads, POS text variants 'downsize' to a corresponding desktop variant.
 * This redeclares rules in Reefer's base typography config for speediness, but let's try to make the mapping programmatic if the rules stick.
 * To update text that is expected to appear on phones, make changes to ./mobileTypography
 */
const posBaseVariantConfigs: TypographyVariantConfig<BaseTypographyVariant> = {
  body: {
    ...BASE_CONFIG.components.Typography.variants['body'],
    desktopStyles: {
      fontSize: '22px',
      lineHeight: '28px',
    },
    fontWeight: 400,
    mobileStyles: {
      /** Desktop/Body */
      fontSize: '16px',
      lineHeight: '24px',
    },
  },
  header: {
    ...BASE_CONFIG.components.Typography.variants['header'],
    branded: true,
    desktopStyles: {
      fontSize: '28px',
      lineHeight: '36px',
    },
    fontWeight: 400,
    mobileStyles: {
      /** Desktop/Header */
      fontSize: '24px',
      lineHeight: '32px',
    },
  },
  huge: {
    ...BASE_CONFIG.components.Typography.variants['huge'],
    branded: true,
    desktopStyles: {
      fontSize: '64px',
      lineHeight: '76px',
    },
    fontWeight: 500,
    mobileStyles: {
      /** Desktop/Title */
      fontSize: '36px',
      lineHeight: '40px',
    },
  },
  mini: {
    ...BASE_CONFIG.components.Typography.variants['mini'],
    desktopStyles: {
      fontSize: '18px',
      lineHeight: '22px',
    },
    fontWeight: 400,
    mobileStyles: {
      /** Desktop/Body (not Mini) */
      fontSize: '16px',
      lineHeight: '24px',
    },
  },
  section: {
    ...BASE_CONFIG.components.Typography.variants['section'],
    branded: true,
    desktopStyles: {
      fontSize: '24px',
      lineHeight: '30px',
    },
    fontWeight: 400,
    mobileStyles: {
      /** POS/Section (no change; no corresponding desktop variant) */
      fontSize: '24px',
      lineHeight: '30px',
    },
  },
  title: {
    ...BASE_CONFIG.components.Typography.variants['title'],
    branded: true,
    desktopStyles: {
      fontSize: '36px',
      lineHeight: '44px',
    },
    fontWeight: 500,
    mobileStyles: {
      /** Desktop/Title */
      fontSize: '36px',
      lineHeight: '40px',
    },
  },
  caps: {
    ...BASE_CONFIG.components.Typography.variants['caps'],
    desktopStyles: {
      fontSize: '18px',
      lineHeight: '22px',
    },
    fontWeight: 400,
    mobileStyles: {
      fontSize: '13px',
      lineHeight: '16px',
    },
  },
};

const boldPosVariantConfig = ObjectKeys(posBaseVariantConfigs)
  .map<TypographyVariantConfig<BoldTypographyVariant>>((value) => {
    const variantKey = String(`${value}-bold`) as BoldTypographyVariant;
    const obj: TypographyVariantConfig<string> = {
      [variantKey]: {
        ...posBaseVariantConfigs[value],
        fontWeight: 600,
      },
    };
    return obj as TypographyVariantConfig<BoldTypographyVariant>;
  })
  .reduce((prev, curr) => ({ ...prev, ...curr }));

export const posTypographyVariants: TypographyVariantConfig<TypographyVariant> =
  {
    ...posBaseVariantConfigs,
    ...boldPosVariantConfig,
  };

export const posVariantComponentMap: TypographyVariantComponentMap = {
  body: 'p',
  'body-bold': 'p',
  caps: 'p',
  'caps-bold': 'p',
  header: 'h3',
  'header-bold': 'h3',
  huge: 'h1',
  'huge-bold': 'h1',
  mini: 'p',
  'mini-bold': 'p',
  section: 'h4',
  'section-bold': 'h4',
  title: 'h2',
  'title-bold': 'h2',
};
